import React, { useState, useEffect } from 'react';
import {
    ArrowLeft,
    Search,
    Calendar,
    Clock,
    CheckCircle,
    XCircle,
    RefreshCw,
    Filter,
    Download,
    Eye,
    MessageSquare,
    Send,
    AlertCircle,
    Loader2,
    RotateCcw,
    CalendarRange,
    Phone,
    User,
    Hash,
    List // Added for page size icon
} from 'lucide-react';
import { apiFetch } from '../api';
import AnimatedBackground from '../components/Background';
import SMSDetails from './SMSDetails';
import { ensureSocketConnection } from '../websocket/socketUtils';
import { useWebSocket } from '../websocket/WebSocketContext';
import ExportBlockedModal from '../components/Exportblockedmodal';

export default function SMSHistory({ onBack, setError, API_BASE_URL, loginId, deliveryMode }) {
    const { client, connected } = useWebSocket();
    const [smsData, setSmsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);

    // Export-blocked modal
    const [showExportBlocked, setShowExportBlocked] = useState(false);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [referenceSearch, setReferenceSearch] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [appNames, setAppNames] = useState([]);
    const [vendorNames, setVendorNames] = useState([]);
    const [selectedApp, setSelectedApp] = useState('ALL');
    const [selectedVendorName, setSelectedvendorName] = useState('ALL');
    const [isAppNamesLoading, setIsAppNamesLoading] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10); // Now mutable
    const [totalCount, setTotalCount] = useState(0); // Kept for export, removed from UI logic

    const [selectedSms, setSelectedSms] = useState(null);
    const [selectedItems, setSelectedItems] = useState(new Set());

    const fetchAppNames = async () => {
        setIsAppNamesLoading(true);
        if (setError) setError('');

        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    if (setError) setError('Socket connection failed.');
                    setIsAppNamesLoading(false);
                    return;
                }

                const topic = `/topic/getSmsAppName/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
                            const appNames = ['ALL', ...data.Response.map(item => item.appName).filter(Boolean)];
                            setAppNames(appNames);
                            setSelectedApp(prev => prev || appNames[0]);
                        } else {
                            if (setError) setError('Failed to load app names via socket.');
                        }
                    } catch (e) {
                        console.error('Failed to parse app names socket', e);
                        if (setError) setError('Failed to parse app names.');
                        setAppNames(['ALL']);
                        setSelectedApp('ALL');
                    } finally {
                        subscription.unsubscribe();
                        setIsAppNamesLoading(false);
                    }
                });

                client.publish({
                    destination: '/app/getSmsAppName',
                    body: JSON.stringify({})
                });

            } else {
                // --- API LOGIC ---
                const response = await apiFetch(`/getSmsAppName`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();

                if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
                    const appNames = ['ALL', ...data.Response.map(item => item.appName).filter(Boolean)];
                    setAppNames(appNames);
                    setSelectedApp(prev => prev || appNames[0]);
                } else {
                    if (setError) setError('Failed to load app names. Invalid response format.');
                }
                setIsAppNamesLoading(false);
            }
        } catch (err) {
            console.error('Error fetching app names:', err);
            if (setError) setError('Failed to load app names. Network error or API issue.');
            setAppNames(['ALL']);
            setSelectedApp('ALL');
            setIsAppNamesLoading(false);
        }
    };

    const fetchVendorNames = async () => {
        setIsAppNamesLoading(true);
        if (setError) setError('');

        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    if (setError) setError('Socket connection failed.');
                    setIsAppNamesLoading(false);
                    return;
                }

                const topic = `/topic/sms/config/getAll/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
                            const appNames = ['ALL', ...data.Response.map(item => item).filter(Boolean)];
                            setVendorNames(appNames);
                            setSelectedvendorName(prev => prev || appNames[0]);
                        } else {
                            if (setError) setError('Failed to load vendor names via socket.');
                        }
                    } catch (e) {
                        console.error('Failed to parse vendor names socket', e);
                        setVendorNames(['ALL']);
                        setSelectedvendorName('ALL');
                    } finally {
                        subscription.unsubscribe();
                        setIsAppNamesLoading(false);
                    }
                });

                // Publish request with filter
                client.publish({
                    destination: '/app/sms/config/getAll',
                    body: JSON.stringify({ isSmsVendor: true })
                });

            } else {
                // --- API LOGIC ---
                const response = await apiFetch(`/sms/config/getAll?isSmsVendor=true`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();

                if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
                    const appNames = ['ALL', ...data.Response.map(item => item).filter(Boolean)];
                    setVendorNames(appNames);
                    setSelectedvendorName(prev => prev || appNames[0]);
                } else {
                    if (setError) setError('Failed to load vendor names. Invalid response format.');
                }
                setIsAppNamesLoading(false);
            }
        } catch (err) {
            console.error('Error fetching vendor names:', err);
            if (setError) setError('Failed to load vendor names. Network error or API issue.');
            setVendorNames(['ALL']);
            setSelectedvendorName('ALL');
            setIsAppNamesLoading(false);
        }
    };

    const dateToMs = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr + 'T00:00:00').getTime().toString();
    };

    const endDateToMs = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr + 'T23:59:59').getTime().toString();
    };

    const fetchSmsHistory = async (page = currentPage, size = pageSize) => {
        setIsLoading(true);

        // 1. Build Payload (Shared)
        const pageNo = page - 1;
        const filterPayload = {
            pageNo,
            pageSize: size
        };

        if (searchTerm.trim()) filterPayload.searchText = searchTerm.trim();
        if (referenceSearch.trim()) {
            filterPayload.referenceIdOrName = referenceSearch.trim();
        }
        if (selectedApp && selectedApp !== 'ALL') filterPayload.appName = selectedApp;
        if (selectedVendorName && selectedVendorName !== 'ALL') filterPayload.vendorName = selectedVendorName;
        if (statusFilter) filterPayload.status = statusFilter;
        if (startDate) filterPayload.startTimeMs = dateToMs(startDate);
        if (endDate) filterPayload.endTimeMs = endDateToMs(endDate);

        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    if (setError) setError('Socket connection failed.');
                    setIsLoading(false);
                    return;
                }

                const topic = `/topic/sms/filter/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data?.Response && Array.isArray(data.Response)) {
                            setSmsData(data.Response);
                            setTotalCount(data.TOTAL_COUNT || 0);
                        } else {
                            setSmsData([]);
                            setTotalCount(0);
                        }
                    } catch (e) {
                        console.error('Failed to parse SMS history socket', e);
                        if (setError) setError('Failed to parse SMS history.');
                        setSmsData([]);
                        setTotalCount(0);
                    } finally {
                        subscription.unsubscribe();
                        setIsLoading(false);
                    }
                });

                client.publish({
                    destination: '/app/sms/filter',
                    body: JSON.stringify(filterPayload)
                });

            } else {
                // --- API LOGIC ---
                const res = await apiFetch(`/sms/filter`, {
                    method: 'POST',
                    body: JSON.stringify(filterPayload)
                });

                if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                const data = await res.json();

                if (data?.Response && Array.isArray(data.Response)) {
                    setSmsData(data.Response);
                    setTotalCount(data.TOTAL_COUNT || 0);
                } else {
                    setSmsData([]);
                    setTotalCount(0);
                }
                setIsLoading(false);
            }
        } catch (error) {
            console.error('Error fetching SMS history:', error);
            if (setError) setError('Failed to fetch SMS history: ' + error.message);
            setSmsData([]);
            setTotalCount(0);
            setIsLoading(false);
        }
    };

    const handleExport = async () => {

        // Block export in socket/stream mode — show modal instead
        if (deliveryMode === 'socket') {
            setShowExportBlocked(true);
            return;
        }

        if (!startDate || !endDate) {
            setError('Please select both start and end dates for export');
            return;
        }

        setIsExporting(true);
        try {
            const filterPayload = {
                pageNo: 0,
                pageSize: 1000 // Default high limit for export
            };

            if (searchTerm.trim()) filterPayload.searchText = searchTerm.trim();
            if (referenceSearch.trim()) {
                filterPayload.referenceIdOrName = referenceSearch.trim();
            }
            if (selectedApp && selectedApp !== 'ALL') filterPayload.appName = selectedApp;
            if (selectedVendorName && selectedVendorName !== 'ALL') filterPayload.vendorName = selectedVendorName;
            if (statusFilter) filterPayload.status = statusFilter;
            if (startDate) filterPayload.startTimeMs = dateToMs(startDate);
            if (endDate) filterPayload.endTimeMs = endDateToMs(endDate);

            const url = `/sms/filter?export=true`;

            const res = await apiFetch(url, {
                method: 'POST',
                body: JSON.stringify(filterPayload)
            });

            if (!res.ok) {
                throw new Error(`Export failed: ${res.status} ${res.statusText}`);
            }

            const data = await res.json();

            if (data.STATUS === 'SUCCESS' && data.Response) {
                const byteCharacters = atob(data.Response);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `sms-history-${startDate}-to-${endDate}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                throw new Error('Export failed: Invalid response format');
            }

        } catch (error) {
            console.error('Error exporting data:', error);
            setError('Failed to export data: ' + error.message);
        } finally {
            setIsExporting(false);
        }
    };


    const handleResetFilters = () => {
        setSearchTerm('');
        setReferenceSearch('');
        setStartDate('');
        setEndDate('');
        setStatusFilter('');
        setSelectedApp('ALL');
        setSelectedvendorName('ALL');
        setSelectedItems(new Set());
        setCurrentPage(1);
        setPageSize(10); // Reset page size
    };

    useEffect(() => {
        // Don't fetch until selectedApp is initialized
        if (!selectedApp) {
            return;
        }

        // Debounce for text searches, immediate for dropdown/date filters
        const delay = searchTerm || referenceSearch ? 500 : 0;

        const debounceTimer = setTimeout(() => {
            fetchSmsHistory(currentPage, pageSize);
        }, delay);

        return () => clearTimeout(debounceTimer);
    }, [searchTerm, referenceSearch, startDate, endDate, statusFilter, selectedApp, selectedVendorName, currentPage, pageSize]);

    // Initial load - fetch app names and vendor names only
    useEffect(() => {
        fetchAppNames();
        fetchVendorNames();
    }, []);

    const handleRefresh = () => {
        fetchSmsHistory(currentPage, pageSize);
        fetchAppNames();
        fetchVendorNames();
    };

    const handlePageSizeChange = (e) => {
        const newSize = parseInt(e.target.value, 10);
        setIsLoading(true); // <--- Lock UI immediately
        setPageSize(newSize);
        setCurrentPage(1);
    };

    const handlePreviousPage = () => {
        // Only proceed if not currently loading
        if (currentPage > 1 && !isLoading) {
            setIsLoading(true); // <--- Lock UI immediately
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        // Only proceed if we have a full page of data AND not currently loading
        if (smsData.length >= pageSize && !isLoading) {
            setIsLoading(true); // <--- Lock UI immediately
            setCurrentPage(currentPage + 1);
        }
    };

    const getStatusColor = (response, status) => {
        if (status === 'SUCCESS' || response?.toLowerCase().includes('successfully')) return 'text-green-400';
        if (status === 'FAILED' || response?.toLowerCase().includes('error')) return 'text-red-400';
        return 'text-yellow-400';
    };

    const getStatusIcon = (response, status) => {
        if (status === 'SUCCESS' || response?.toLowerCase().includes('successfully')) return CheckCircle;
        if (status === 'FAILED' || response?.toLowerCase().includes('error')) return XCircle;
        return AlertCircle;
    };

    const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        if (phone.length === 10) return phone.replace(/(\d{5})(\d{5})/, '$1 $2');
        return phone;
    };

    const truncateMessage = (message, maxLength = 100) => {
        if (!message) return '';
        return message.length > maxLength ? message.substring(0, maxLength) + '...' : message;
    };

    const handleItemSelect = (id) => {
        const newSelected = new Set(selectedItems);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedItems(newSelected);
    };

    const handleSelectAll = () => {
        if (selectedItems.size === smsData.length) {
            setSelectedItems(new Set());
        } else {
            setSelectedItems(new Set(smsData.map(item => item._id)));
        }
    };

    const isExportEnabled = startDate && endDate && !isExporting;

    const getMobileNumber = (sms) => {
        if (sms.smsVendor === 'PAYTM' && sms.bulkSms?.[0]) {
            return sms.bulkSms[0].smsData?.receiver || sms.bulkSms[0]?.receiver;
        } else if (sms.sms_bulk_type && sms.bulkSms?.[0]) {
            return sms.bulkSms[0].mobileNo;
        } else if (sms.sms?.mobileNo) {
            return sms.sms.mobileNo;
        }
        return 'N/A';
    };

    const getSmsMessage = (sms) => {
        if (sms.smsVendor === 'PAYTM' && sms.bulkSms?.[0]) {
            return sms.bulkSms[0].smsData?.content;
        } else if (sms.sms_bulk_type && sms.bulkSms?.[0]) {
            return sms.bulkSms[0].smsTemplate;
        } else if (sms.sms?.smsTemplate) {
            return sms.sms.smsTemplate;
        }
        return 'N/A';
    };

    const getAppName = (sms) => {
        if (sms.smsVendor === 'PAYTM' && sms.bulkSms?.[0]) {
            return sms.bulkSms[0].appName || sms.appName;
        } else if (sms.sms_bulk_type && sms.bulkSms?.[0]) {
            return sms.bulkSms[0].appName;
        } else if (sms.sms?.appName) {
            return sms.sms.appName;
        }
        return sms.appName || 'N/A';
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 to-[#649EE8]">
            <AnimatedBackground />

            {/* Export-blocked modal — shown when in socket mode */}
            <ExportBlockedModal
                isOpen={showExportBlocked}
                onClose={() => setShowExportBlocked(false)}
                feature="Sms Export"
            />

            {/* Header */}
            <header className="bg-white/10 backdrop-blur-md border-b border-white/20 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={onBack}
                                className="flex items-center space-x-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all duration-200"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                <span>Back to Dashboard</span>
                            </button>
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                                    <MessageSquare className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-white">SMS History</h1>
                                    <p className="text-sm text-white/60">SMS Message History</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center space-x-4">
                            <button
                                onClick={handleExport}
                                disabled={!isExportEnabled && deliveryMode !== 'socket'}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${deliveryMode === 'socket'
                                    ? 'bg-orange-500/20 text-orange-200 hover:bg-orange-500/30 cursor-pointer'
                                    : isExportEnabled
                                        ? 'bg-green-500/20 text-green-200 hover:bg-green-500/30'
                                        : 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
                                    }`}
                            >
                                {isExporting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Download className="w-4 h-4" />
                                )}
                                <span>{isExporting ? 'Exporting...' : 'Export'}</span>
                                {deliveryMode === 'socket' && (
                                    <span className="ml-1 text-[10px] font-bold bg-orange-500/30 text-orange-300 px-1.5 py-0.5 rounded-full border border-orange-500/30">
                                        API only
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Filters */}
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Search */}
                        <div>
                            <label className="block text-white/80 text-sm font-medium mb-2">Search</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search SMS messages..."
                                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        {/* Search by Reference ID or Name */}
                        <div>
                            <label className="block text-white/80 text-sm font-medium mb-2">Reference ID or Name</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
                                <input
                                    type="text"
                                    value={referenceSearch}
                                    onChange={(e) => setReferenceSearch(e.target.value)}
                                    placeholder="Search by reference..."
                                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                />
                            </div>
                        </div>

                        {/* Start Date */}
                        <div>
                            <label className="block text-white/80 text-sm font-medium mb-2">Start Date</label>
                            <div className="relative">
                                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        {/* End Date */}
                        <div>
                            <label className="block text-white/80 text-sm font-medium mb-2">End Date</label>
                            <div className="relative">
                                <CalendarRange className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        {/* Status Filter */}
                        <div>
                            <label className="block text-white/80 text-sm font-medium mb-2">Status</label>
                            <div className="relative">
                                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="" className="bg-slate-800">All Status</option>
                                    <option value="SUCCESS" className="bg-slate-800">Success</option>
                                    <option value="FAILED" className="bg-slate-800">Failed</option>
                                    <option value="PENDING" className="bg-slate-800">Pending</option>
                                </select>
                            </div>
                        </div>

                        {/* App Name Filter */}
                        <div>
                            <label className="block text-white/80 text-sm font-medium mb-2">App Name</label>
                            <div className="relative">
                                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
                                <select
                                    value={selectedApp}
                                    onChange={(e) => setSelectedApp(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {appNames.length === 0 ? (
                                        <option className="bg-slate-800" disabled>Loading...</option>
                                    ) : (
                                        appNames.map((app, index) => (
                                            <option key={index} value={app} className="bg-slate-800">
                                                {app === 'ALL' ? 'All Apps' : app}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>
                        </div>
                        {/* Vendor Name Filter */}
                        <div>
                            <label className="block text-white/80 text-sm font-medium mb-2">Vendor Name</label>
                            <div className="relative">
                                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
                                <select
                                    value={selectedVendorName}
                                    onChange={(e) => setSelectedvendorName(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {vendorNames.length === 0 ? (
                                        <option className="bg-slate-800" disabled>Loading...</option>
                                    ) : (
                                        vendorNames.map((app, index) => (
                                            <option key={index} value={app} className="bg-slate-800">
                                                {app === 'ALL' ? 'All Apps' : app}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2 mt-4">
                        <button
                            onClick={handleRefresh}
                            disabled={isLoading}
                            className="flex items-center space-x-2 px-4 py-2 bg-purple-500/20 text-purple-200 rounded-lg hover:bg-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                        >
                            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                            <span>Refresh</span>
                        </button>
                        <button
                            onClick={handleResetFilters}
                            className="flex items-center space-x-2 px-4 py-2 bg-orange-500/20 text-orange-200 rounded-lg hover:bg-orange-500/30 transition-all duration-200"
                        >
                            <RotateCcw className="w-4 h-4" />
                            <span>Reset</span>
                        </button>
                    </div>

                    {/* Export Info */}
                    {!isExportEnabled && (
                        <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                            <div className="flex items-center space-x-2">
                                <AlertCircle className="w-4 h-4 text-yellow-400" />
                                <span className="text-yellow-200 text-sm">
                                    Please select both start and end dates to enable export functionality
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Results */}
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl overflow-hidden">
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-white/20">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <h2 className="text-xl font-bold text-white">
                                    SMS Messages
                                    <span className="ml-3 text-sm text-white/60">
                                        Showing {smsData.length} record{smsData.length !== 1 ? 's' : ''}
                                    </span>
                                </h2>
                                {/* Removed total count display */}
                                {smsData.length > 0 && (
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={selectedItems.size === smsData.length && smsData.length > 0}
                                            onChange={handleSelectAll}
                                            className="rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-500"
                                        />
                                        <span className="text-white/60 text-sm">
                                            {selectedItems.size > 0 ? `${selectedItems.size} selected` : 'Select all'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Loading State */}
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="flex items-center space-x-3">
                                <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
                                <span className="text-white/80">Loading SMS history...</span>
                            </div>
                        </div>
                    ) : smsData.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <AlertCircle className="w-12 h-12 text-white/40 mb-4" />
                            <p className="text-white/60 text-lg">No SMS messages found</p>
                            <p className="text-white/40 text-sm">Try adjusting your filters</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-white/10">
                            {smsData.map((sms) => {
                                const StatusIcon = getStatusIcon(sms.sms_response, sms.STATUS);
                                const mobileNumber = getMobileNumber(sms);
                                const message = getSmsMessage(sms);
                                const appName = getAppName(sms);

                                return (
                                    <div
                                        key={sms._id}
                                        className="px-6 py-4 hover:bg-white/5 transition-all duration-200"
                                    >
                                        <div className="flex items-start space-x-4">
                                            <div className="flex-shrink-0 pt-1">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedItems.has(sms._id)}
                                                    onChange={() => handleItemSelect(sms._id)}
                                                    className="rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-500"
                                                />
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center space-x-3 mb-2">
                                                    <div className="flex-shrink-0">
                                                        <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                                                            {sms.sms_bulk_type ? <Send className="w-5 h-5 text-white" /> : <MessageSquare className="w-5 h-5 text-white" />}
                                                        </div>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center space-x-2 mb-1">
                                                            <h3 className="text-white font-semibold">
                                                                {sms.sms_bulk_type ? 'Bulk SMS' : 'Single SMS'}
                                                            </h3>
                                                            {sms.sms_bulk_type && (
                                                                <span className="px-2 py-1 bg-blue-500/20 text-blue-200 text-xs rounded-full">
                                                                    BULK
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-white/60 text-sm truncate">
                                                            {truncateMessage(message, 80)}
                                                        </p>
                                                    </div>
                                                    <div className="flex-shrink-0 flex items-center space-x-2">
                                                        <StatusIcon className={`w-5 h-5 ${getStatusColor(sms.sms_response, sms.STATUS)}`} />
                                                        <span className={`text-sm font-medium ${getStatusColor(sms.sms_response, sms.STATUS)}`}>
                                                            {sms.STATUS}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                                                    <div>
                                                        <p className="text-white/40 text-xs uppercase tracking-wide">Mobile Number</p>
                                                        <div className="flex items-center space-x-2">
                                                            <Phone className="w-4 h-4 text-white/60" />
                                                            <p className="text-white/80 text-sm font-mono">
                                                                {formatPhoneNumber(mobileNumber)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <p className="text-white/40 text-xs uppercase tracking-wide">App Name</p>
                                                        <div className="flex items-center space-x-2">
                                                            <User className="w-4 h-4 text-white/60" />
                                                            <p className="text-white/80 text-sm">{appName}</p>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <p className="text-white/40 text-xs uppercase tracking-wide">SMS Vendor</p>
                                                        <div className="flex items-center space-x-2">
                                                            <Hash className="w-4 h-4 text-white/60" />
                                                            <p className="text-white/80 text-sm">{sms.smsVendor || 'N/A'}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between mt-3">
                                                    <div className="flex items-center space-x-4 text-white/60 text-sm">
                                                        <div className="flex items-center space-x-1">
                                                            <Clock className="w-4 h-4" />
                                                            <span>{sms.creation_time_str}</span>
                                                        </div>
                                                        <div className="flex items-center space-x-1">
                                                            <Calendar className="w-4 h-4" />
                                                            <span>{sms.date}</span>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => setSelectedSms(sms)}
                                                        className="flex items-center space-x-1 px-3 py-1 bg-purple-500/20 text-purple-200 rounded-lg hover:bg-purple-500/30 transition-all duration-200 text-sm"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                        <span>View Details</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Pagination */}
                    {!isLoading && smsData.length > 0 && (
                        <div className="px-6 py-4 border-t border-white/20 bg-white/5">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">

                                {/* Page Size Selector */}
                                <div className="flex items-center space-x-3">
                                    <div className="flex items-center space-x-2 text-white/60 text-sm">
                                        <List className="w-4 h-4" />
                                        <span>Rows per page:</span>
                                    </div>
                                    <select
                                        value={pageSize}
                                        onChange={handlePageSizeChange}
                                        className="bg-white/10 border border-white/20 text-white rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                    >
                                        <option value={10} className="bg-slate-800">10</option>
                                        <option value={25} className="bg-slate-800">25</option>
                                        <option value={50} className="bg-slate-800">50</option>
                                        <option value={100} className="bg-slate-800">100</option>
                                    </select>
                                </div>

                                {/* Pagination Controls */}
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={handlePreviousPage}
                                        disabled={currentPage === 1 || isLoading}
                                        className="px-3 py-1 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-white/80 px-3 py-1 font-medium">
                                        Page {currentPage}
                                    </span>
                                    <button
                                        onClick={handleNextPage}
                                        disabled={smsData.length < pageSize || isLoading}
                                        className="px-3 py-1 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Detail Modal - Using SMSDetails Component */}
                <SMSDetails
                    selectedSms={selectedSms}
                    onClose={() => setSelectedSms(null)}
                    setError={setError}
                    loginId={loginId}
                    deliveryMode={deliveryMode}
                />
            </div>
        </div>
    );
}